/* eslint-disable @atlassian/tangerine/import/no-parent-imports */
import { useMemo } from 'react';

import { ISSUE_NAVIGATOR_URL } from '../../../../common/constants/issue-navigator';
import { useAppContext } from '../../../../common/ui/app-context';
import { INTENT_DETECTION, IntentMode } from '../../../../common/utils/intent-detection';
import { getWorkItemPageUrl } from '../../../../common/utils/quick-find/jira-work-item-navigation';
import { useSelectedFilters } from '../../../../controllers/filters/selected';

import { isSamePathname } from './utils';

export type IntentRoutesResult = {
	defaultMode: IntentMode;
	allAppsUrl: string;
	jiraWorkItemsUrl: string;
	isAllAppsPage: boolean;
	isJiraWorkItemsPage: boolean;
};

/**
 * Determine the default mode and URLs for the intent routing.
 */
export const useIntentRoutes = ({
	query,
	minQueryLength,
}: {
	query: string;
	minQueryLength: number;
}): IntentRoutesResult => {
	const { generateSearchUrl, queryParams } = useAppContext();
	const {
		selectedFilters: { jiraFilters },
	} = useSelectedFilters({});

	const allAppsUrl = useMemo(
		() =>
			generateSearchUrl({
				...queryParams,
				text: query,
			}),
		[generateSearchUrl, queryParams, query],
	);

	const jiraWorkItemsUrl = useMemo(() => {
		try {
			return getWorkItemPageUrl({
				query,
				jiraFilters,
				generateSearchUrl,
				queryParams,
			});
		} catch {
			return ISSUE_NAVIGATOR_URL;
		}
	}, [generateSearchUrl, jiraFilters, queryParams, query]);

	const currentUrl = typeof window !== 'undefined' ? window.location.href : '';

	const isAllAppsPage = useMemo(
		() => (currentUrl ? isSamePathname(currentUrl, allAppsUrl) : false),
		[currentUrl, allAppsUrl],
	);
	const isJiraWorkItemsPage = useMemo(
		() => (currentUrl ? isSamePathname(currentUrl, jiraWorkItemsUrl) : false),
		[currentUrl, jiraWorkItemsUrl],
	);

	let defaultMode = isAllAppsPage ? IntentMode.FPS : IntentMode.GNIN;

	// Hydrate the default mode from the intent detection result if available.
	const intentDetectionResult = INTENT_DETECTION.value();
	if (intentDetectionResult) {
		const { mode, query: intentDetectionQuery } = intentDetectionResult;
		// If the intent detection result is for the current page, use it as the default mode.
		if (query.length >= minQueryLength && query === intentDetectionQuery) {
			defaultMode = mode;
		}
	}

	return {
		defaultMode,
		allAppsUrl,
		jiraWorkItemsUrl,
		isAllAppsPage,
		isJiraWorkItemsPage,
	};
};
